import { createFileRoute, Link, notFound, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import {
  useSuspenseInfiniteQuery,
  useSuspenseQuery,
  infiniteQueryOptions,
  queryOptions,
} from "@tanstack/react-query";
import { MAX_LIST_PAGE, parsePageSearch } from "@/lib/search-params";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { listBlogPosts, listBlogCategories } from "@/lib/blog.functions";
import { absoluteUrl } from "@/lib/absolute-url";

// startPage is the ?page= value. A crawler cannot click "Load more", so each
// page of posts also has its own URL, /hair-loss-blog/?page=N, rendered on
// the server. Without it, only the first 12 posts had an internal link.
const postsInfiniteOptions = (q?: string, categorySlug?: string, startPage = 1) =>
  infiniteQueryOptions({
    queryKey: ["blog-posts-infinite", { q, categorySlug, startPage }],
    queryFn: ({ pageParam }) =>
      listBlogPosts({ data: { page: pageParam as number, q, categorySlug } }),
    initialPageParam: startPage,
    getNextPageParam: (last) =>
      last.page * last.pageSize < last.total ? last.page + 1 : undefined,
  });

const categoriesOptions = queryOptions({
  queryKey: ["blog-categories"],
  queryFn: () => listBlogCategories(),
});

export const Route = createFileRoute("/hair-loss-blog/")({
  validateSearch: parsePageSearch,
  loaderDeps: ({ search }) => ({ s: search.s, category: search.category, page: search.page ?? 1 }),
  loader: async ({ context, deps }) => {
    if (deps.page > MAX_LIST_PAGE) throw notFound();
    const [posts] = await Promise.all([
      context.queryClient.ensureInfiniteQueryData(
        postsInfiniteOptions(deps.s, deps.category, deps.page),
      ),
      context.queryClient.ensureQueryData(categoriesOptions),
    ]);
    // A page number past the last post is not a real page.
    if (deps.page > 1 && (posts.pages[0]?.posts.length ?? 0) === 0) throw notFound();
  },
  head: ({ match }) => {
    const page = match.search.page ?? 1;
    const pageLabel = page > 1 ? `, Page ${page}` : "";
    const canonical = page > 1 ? `/hair-loss-blog/?page=${page}` : "/hair-loss-blog/";
    const description = `Expert articles on hair loss, hair transplants, FUE, and restoration techniques from the RESTORE Hair team.${page > 1 ? ` Page ${page}.` : ""}`;
    return {
      meta: [
        { title: `Hair Loss Blog${pageLabel} | RESTORE Hair` },
        { name: "description", content: description },
        { property: "og:title", content: `Hair Loss Blog${pageLabel} | RESTORE Hair` },
        { property: "og:description", content: description },
        // og:url must match the canonical, which keeps ?page= on older pages.
        { property: "og:url", content: absoluteUrl(canonical) },
      ],
      // Each page of posts is its own canonical URL, so search engines keep
      // following the links to the older posts.
      links: [{ rel: "canonical", href: absoluteUrl(canonical) }],
    };
  },
  component: BlogIndex,
  errorComponent: ({ error, reset }) => (
    <div className="mx-auto max-w-4xl px-4 py-20 text-center">
      <h1 className="text-2xl font-semibold text-foreground">Couldn't load the blog</h1>
      <p className="mt-2 text-sm text-muted-foreground">Something went wrong. Please try again.</p>
      <button onClick={reset} className="mt-4 rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground">Retry</button>
    </div>
  ),
  notFoundComponent: () => <div className="p-10 text-center">Not found</div>,
});

function BlogIndex() {
  const { s, category, page } = Route.useSearch();
  const navigate = useNavigate();
  const [term, setTerm] = useState(s ?? "");

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useSuspenseInfiniteQuery(postsInfiniteOptions(s, category, page ?? 1));
  const { data: categories } = useSuspenseQuery(categoriesOptions);

  const posts = data.pages.flatMap((p) => p.posts);
  const nextPage = (data.pages[data.pages.length - 1]?.page ?? 1) + 1;
  const nextPageParams = new URLSearchParams();
  if (s) nextPageParams.set("s", s);
  if (category) nextPageParams.set("category", category);
  nextPageParams.set("page", String(nextPage));
  const nextPageHref = `/hair-loss-blog/?${nextPageParams.toString()}`;
  const total = data.pages[0]?.total ?? 0;
  const activeCategory = categories.find((c) => c.slug === category);

  const setSearch = (next: { s?: string; category?: string }) => {
    navigate({ to: "/hair-loss-blog/", search: next });
  };

  return (
    <>
      <Header />
      <main className="mx-auto max-w-6xl px-4 py-12 lg:py-16">
        <header className="mb-8 text-center">
          <h1 className="text-4xl font-bold tracking-tight text-foreground lg:text-5xl">Hair Loss Blog</h1>
          <p className="mx-auto mt-3 max-w-2xl text-base text-muted-foreground">
            Insights, research, and patient stories from the RESTORE Hair team.
          </p>

          <form
            className="mx-auto mt-6 flex w-full max-w-xl items-center gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              const v = term.trim();
              setSearch({ s: v.length > 0 ? v : undefined, category });
            }}
          >
            <input
              type="search"
              value={term}
              onChange={(e) => setTerm(e.target.value)}
              placeholder="Search articles…"
              aria-label="Search articles"
              className="h-11 flex-1 rounded-md border border-border bg-background px-4 text-sm text-foreground outline-none focus:ring-2 focus:ring-primary"
            />
            <button type="submit" className="h-11 rounded-md bg-primary px-5 text-sm font-semibold text-primary-foreground hover:opacity-90">
              Search
            </button>
          </form>

          <div className="mt-6 flex flex-wrap items-center justify-center gap-2">
            <button
              type="button"
              onClick={() => setSearch({ s })}
              className={`rounded-full border px-4 py-1.5 text-sm transition-colors ${
                !category
                  ? "border-primary bg-primary text-primary-foreground"
                  : "border-border text-foreground hover:bg-accent"
              }`}
            >
              All
            </button>
            {categories.map((c) => (
              <button
                key={c.slug}
                type="button"
                onClick={() => setSearch({ s, category: c.slug })}
                className={`rounded-full border px-4 py-1.5 text-sm transition-colors ${
                  category === c.slug
                    ? "border-primary bg-primary text-primary-foreground"
                    : "border-border text-foreground hover:bg-accent"
                }`}
              >
                {c.name}
              </button>
            ))}
          </div>

          {s || category ? (
            <p className="mx-auto mt-5 max-w-2xl text-sm text-muted-foreground">
              {total} {total === 1 ? "article" : "articles"}
              {s ? <> matching &ldquo;{s}&rdquo;</> : null}
              {activeCategory ? <> in {activeCategory.name}</> : null}{" "}
              <Link
                to="/hair-loss-blog/"
                search={{}}
                onClick={() => setTerm("")}
                className="font-semibold text-primary hover:underline"
              >
                Clear
              </Link>
            </p>
          ) : null}
        </header>

        {posts.length === 0 ? (
          <p className="text-center text-muted-foreground">No articles matched. Try a different search or category.</p>
        ) : (
          <ul className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
            {posts.map((p) => (
              <li key={p.id} className="group flex flex-col overflow-hidden rounded-lg border border-border bg-card transition-shadow hover:shadow-lg">
                <Link to="/$slug/" params={{ slug: p.slug }} className="flex h-full flex-col">
                  {p.featured_image_url ? (
                    <div className="relative aspect-[16/9] overflow-hidden bg-foreground">
                      <img src={p.featured_image_url} alt={p.title} loading="lazy" className="h-full w-full object-cover transition-transform group-hover:scale-105" />
                      <h2 className="absolute inset-y-0 left-0 z-10 flex w-[43%] items-center px-3 text-sm font-semibold leading-snug text-background sm:px-4 sm:text-base">
                        {p.title}
                      </h2>
                    </div>
                  ) : null}
                  <div className="flex flex-1 flex-col gap-2 p-5">
                    {!p.featured_image_url ? (
                      <h2 className="text-lg font-semibold leading-snug text-foreground group-hover:text-primary">{p.title}</h2>
                    ) : null}
                    {p.excerpt ? <p className="line-clamp-3 text-sm text-muted-foreground">{p.excerpt}</p> : null}
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )}

        {hasNextPage ? (
          <div className="mt-12 flex justify-center">
            {/* A real link, so crawlers reach the older posts. Visitors with
                JavaScript get the posts added in place. */}
            <a
              href={nextPageHref}
              onClick={(e) => {
                e.preventDefault();
                if (!isFetchingNextPage) void fetchNextPage();
              }}
              aria-disabled={isFetchingNextPage}
              className="rounded-md border border-border px-6 py-3 text-sm font-semibold text-foreground transition-colors hover:bg-accent aria-disabled:opacity-60"
            >
              {isFetchingNextPage ? "Loading…" : "Load more articles"}
            </a>
          </div>
        ) : null}

        {!s && !category ? (
          <PageLinks current={page ?? 1} pageCount={Math.ceil(total / (data.pages[0]?.pageSize ?? 1))} />
        ) : null}
      </main>
      <Footer />
    </>
  );
}

// A link to every page of posts. "Load more" links only to the next page, so
// each page had one internal link (Ahrefs, 3 Oct 2026). Page 1 is the plain
// /hair-loss-blog/ URL, its canonical.
function PageLinks({ current, pageCount }: { current: number; pageCount: number }) {
  if (pageCount < 2) return null;
  return (
    <nav aria-label="Blog pages" className="mt-8 flex flex-wrap justify-center gap-2 text-sm">
      {Array.from({ length: pageCount }, (_, i) => i + 1).map((n) =>
        n === current ? (
          <span key={n} aria-current="page" className="rounded-md border border-primary bg-primary px-3 py-1.5 font-semibold text-primary-foreground">
            {n}
          </span>
        ) : (
          <a
            key={n}
            href={n === 1 ? "/hair-loss-blog/" : `/hair-loss-blog/?page=${n}`}
            className="rounded-md border border-border px-3 py-1.5 text-foreground transition-colors hover:bg-accent"
          >
            {n}
          </a>
        ),
      )}
    </nav>
  );
}
